import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import type { IntakeServiceId } from '../types';

export interface IntakeHubOrgServiceOption {
  id: IntakeServiceId;
  label: string;
  description?: string;
}

export interface IntakeHubOrgServiceEnrollmentControlProps {
  /** Every service the organization may be enrolled in (retired ones excluded). */
  serviceOptions: IntakeHubOrgServiceOption[];
  /** The live org-level set from the server. Anything absent is un-enrolled. */
  enrolledServiceKeys: IntakeServiceId[];
  /** Receives the COMPLETE next set; keys left out are un-enrolled org-wide. */
  onSave: (serviceKeys: IntakeServiceId[]) => void;
  isSaving?: boolean;
  isLoading?: boolean;
  /** Disables saving independent of the selection (e.g. a moderator without admin). */
  isDisabled?: boolean;
  disabledReason?: string;
  /** Providers the last save could not reconcile. Rendered as a warning. */
  failedUserIds?: string[];
  errorMessage?: string;
}

function sameSet(a: IntakeServiceId[], b: IntakeServiceId[]) {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  return b.every(key => left.has(key));
}

/**
 * The organization-level Intake Hub service control (GDN-3404).
 *
 * Enrollment is declarative: the POST carries the complete set, and a key left
 * out un-enrolls (pauses) that service for every provider in the organization.
 * That makes an accidental empty save destructive, so clearing the last
 * checkbox requires an explicit confirmation before anything is sent.
 */
export function IntakeHubOrgServiceEnrollmentControl({
  serviceOptions,
  enrolledServiceKeys,
  onSave,
  isSaving = false,
  isLoading = false,
  isDisabled = false,
  disabledReason,
  failedUserIds = [],
  errorMessage,
}: IntakeHubOrgServiceEnrollmentControlProps) {
  // An organization can be enrolled in a service that has since been retired.
  // Such a key has no checkbox — and POSTing it back would be rejected, since
  // the backend validates against the active definitions — so it is held out
  // of the editable selection and reported separately. Saving drops it, which
  // is the only way to correct the row from here.
  const offerableServiceIds = useMemo(
    () => new Set(serviceOptions.map(service => service.id)),
    [serviceOptions],
  );
  const seededServiceIds = useMemo(
    () => enrolledServiceKeys.filter(key => offerableServiceIds.has(key)),
    [enrolledServiceKeys, offerableServiceIds],
  );
  // Only meaningful against a catalog we actually have. With no options at
  // all the catalog is "not loaded", never "everything you are enrolled in has
  // been retired" — and reading it the second way would raise a false alert,
  // make Save reachable, and turn one confirming click into an org-wide
  // un-enroll.
  const retiredEnrolledServiceIds = useMemo(
    () =>
      serviceOptions.length === 0
        ? []
        : enrolledServiceKeys.filter(key => !offerableServiceIds.has(key)),
    [enrolledServiceKeys, offerableServiceIds, serviceOptions.length],
  );

  const [selectedServiceIds, setSelectedServiceIds] = useState<IntakeServiceId[]>(seededServiceIds);
  const [isConfirmingEmpty, setIsConfirmingEmpty] = useState(false);

  // Re-seed from the server whenever the live set changes — after a save, and
  // after another admin's change lands in the cache. Keyed on the joined set so
  // a new array identity with the same contents doesn't discard local edits.
  const seededKey = [...seededServiceIds].sort().join(',');
  useEffect(() => {
    setSelectedServiceIds(seededServiceIds);
    setIsConfirmingEmpty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seededKey]);

  const isDirty = useMemo(
    () => !sameSet(selectedServiceIds, seededServiceIds),
    [seededServiceIds, selectedServiceIds],
  );
  // Saving is also the retry for a partial fan-out, and the only way to shed a
  // retired key — neither of which makes the checkboxes dirty.
  const canSave = isDirty || retiredEnrolledServiceIds.length > 0 || failedUserIds.length > 0;
  // Every condition that prohibits a write, in one place. The confirmation
  // dialog is a second path to `onSave` and the most destructive one — it
  // posts the empty set — so it has to answer to exactly the same conditions
  // as the main button, including ones that only become true after it opens.
  // Nothing to choose from means nothing can be saved: an empty catalog cannot
  // produce a set worth POSTing, and the only set it could produce is the
  // destructive empty one.
  const isWriteBlocked = isLoading || isSaving || isDisabled || serviceOptions.length === 0;
  const canSubmit = !isWriteBlocked && canSave;

  // A dialog opened while writing was allowed must not survive into a state
  // where it is not: the baseline starting to load or becoming unreadable, a
  // change of authorization, or another save beginning.
  useEffect(() => {
    if (isWriteBlocked) setIsConfirmingEmpty(false);
  }, [isWriteBlocked]);

  const handleToggle = (serviceId: IntakeServiceId) => {
    setSelectedServiceIds(current =>
      current.includes(serviceId)
        ? current.filter(currentId => currentId !== serviceId)
        : [...current, serviceId],
    );
  };

  const handleSaveClick = () => {
    if (!canSubmit) return;
    if (selectedServiceIds.length === 0) {
      setIsConfirmingEmpty(true);
      return;
    }
    onSave(selectedServiceIds);
  };

  const handleConfirmEmpty = () => {
    // Guarded as well as disabled: un-enrolling the whole organization is the
    // one action worth checking twice.
    if (!canSubmit) return;
    setIsConfirmingEmpty(false);
    onSave([]);
  };

  return (
    <Stack spacing={1} sx={{ width: '100%' }}>
      <Box>
        <Typography variant="subtitle2">Services included in intake</Typography>
        <Typography variant="caption" color="text.secondary">
          Choose which services this organization’s intake covers.
        </Typography>
      </Box>

      <Stack direction="row" sx={{ flexWrap: 'wrap', columnGap: 2 }}>
        {serviceOptions.map(service => (
          <FormControlLabel
            key={service.id}
            control={
              <Checkbox
                size="small"
                checked={selectedServiceIds.includes(service.id)}
                disabled={isLoading || isSaving || isDisabled}
                onChange={() => handleToggle(service.id)}
              />
            }
            label={service.label}
          />
        ))}
      </Stack>

      {retiredEnrolledServiceIds.length > 0 && (
        <Alert severity="warning" sx={{ py: 0 }}>
          This organization is still enrolled in {retiredEnrolledServiceIds.join(', ')}, which
          {retiredEnrolledServiceIds.length === 1 ? ' has' : ' have'} been retired and can no longer
          be assigned. Saving removes {retiredEnrolledServiceIds.length === 1 ? 'it' : 'them'}.
        </Alert>
      )}

      {failedUserIds.length > 0 && (
        <Alert severity="warning" sx={{ py: 0 }}>
          The organization was updated, but {failedUserIds.length} provider
          {failedUserIds.length === 1 ? '' : 's'} could not be reconciled:{' '}
          {failedUserIds.join(', ')}. Save again to retry.
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ py: 0 }}>
          {errorMessage}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Button size="small" variant="contained" disabled={!canSubmit} onClick={handleSaveClick}>
          {isSaving ? 'Saving...' : 'Save services'}
        </Button>
        {isDisabled && disabledReason && (
          <Typography variant="caption" color="text.secondary">
            {disabledReason}
          </Typography>
        )}
      </Stack>

      <Dialog open={isConfirmingEmpty} onClose={() => setIsConfirmingEmpty(false)}>
        <DialogTitle>Un-enroll every service?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This pauses every Intake Hub service for every provider in this organization. Providers
            lose access to their intake entirely; the existing items stay visible to staff. You can
            re-enroll at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmingEmpty(false)}>Cancel</Button>
          <Button color="error" disabled={!canSubmit} onClick={handleConfirmEmpty}>
            Un-enroll everything
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
